import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type MouseEvent,
  type ReactNode,
  type SetStateAction,
} from "react";
import Star from "@mui/icons-material/Star";
import StarBorder from "@mui/icons-material/StarBorder";
import {
  Avatar,
  Box,
  Fade,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { FormikProps } from "formik";
import { Formik } from "formik";
import debounce from "lodash-es/debounce.js";
import { useTranslation } from "react-i18next";
import type { NavigateFunction } from "react-router";
import { v4 } from "uuid";
import type { InferType } from "yup";

import type {
  AddFavouriteRecipientRequest,
  CreateWorkItemAction,
  FormContextData,
  MentalHealthCareProvider,
  UserOrTeamSearchResult,
  UserSearchResult,
  UUID,
} from "@thalamos/aspire-common";

import { Banner, BannerList } from "../design-system/Banner/Banner.js";
import { Dropdown } from "../design-system/Dropdown/Dropdown.js";
import { Button } from "../design-system/index.js";
import { TextField } from "../design-system/TextField/TextField.js";
import { Typeahead } from "../design-system/Typeahead/Typeahead.js";
import { stringAvatar } from "../layout/menu/helpers/index.js";
import type {
  AssignmentSearchResponse,
  FormDetails,
  FormikInitialValues,
  FormikNewFormAssigneeValues,
  NewResultWithoutAccreditation,
  Result,
} from "./formAssigneeTypes.js";
import { isAnsweredSearch, newFormAssigneeData } from "./formAssigneeTypes.js";
import { getRecipientsOutsideDataController } from "~/components/DataControllerWarningBanners/get-recipients-outside-data-controller.js";
import { DataControllerWarningBanners } from "~/components/DataControllerWarningBanners/index.js";
import { LoggedInUserContext, ManagedLocationsContext } from "~/Contexts.js";
import {
  addFavouriteRecipient,
  removeFavouriteRecipient,
} from "~/helpers/favouriteRecipients.js";
import { useScreenDetection } from "~/hooks/ScreenDetection/useScreenDetection.js";
import {
  AddRecipientButton,
  handleAddRecipient,
  isShareOutsideDataControllerFn,
  isShareToIndependentFn,
  mapGuestAccreditation,
  RecipientsList,
  SendRequestButton,
  shareToNonTrainingUserFromTrainingFn,
  shareToTrainingUserFromNonTrainingFn,
  TrainingWarningBanners,
  userNotSelectedFn,
} from "~/pages/FormProgressPage/helpers/requestWorkItemSharedHelpers.js";
import { logActionToDatadog } from "~/tracing.js";

// Re-export the relocated shared types/schema so existing import sites that
// reference them via this module continue to work unchanged.
export type {
  FormDetails,
  FormikInitialValues,
  FormikNewFormAssigneeValues,
  NewResultWithoutAccreditation,
  Result,
};
export { newFormAssigneeData };

export function newExpandSearchResults(
  results: UserOrTeamSearchResult[],
  formType: FormContextData["type"],
): NewResultWithoutAccreditation[] {
  const expandedResults: NewResultWithoutAccreditation[] = [];
  for (const result of results) {
    if (result.type === "team") {
      expandedResults.push({
        teamType: result.teamType,
        teamId: result.id,
        email: result.email,
        name: result.name,
        teamName: result.name,
        isTrainingOrganisation: result.isTrainingOrganisation,
        entityType: result.type,
        dataControllerId: result.dataControllerId,
        dataControllerName: result.dataControllerName,
      });
    } else {
      // We can set the team type to null for now if theres more than one team associated to the
      // user as it we set it later when we select a team from the dropdown
      const allowedContexts = result.contexts.filter((m) =>
        isAllowedContext(m, formType),
      );
      const oneContextResult = allowedContexts.length === 1;
      const primaryContext = result.contexts[0];
      expandedResults.push({
        teamId: oneContextResult ? (primaryContext?.id ?? "") : "",
        email: result.email,
        teamType: primaryContext?.teamType,
        contexts: result.contexts,
        name: result.name,
        teamName: oneContextResult
          ? (primaryContext?.name ?? undefined)
          : undefined,
        assignedUserId: result.id,
        isTrainingOrganisation: primaryContext?.isTrainingOrganisation ?? false,
        entityType: result.type,
        dataControllerId: primaryContext?.dataControllerId,
        dataControllerName: primaryContext?.dataControllerName,
        gmcNumber: result.gmcNumber,
        section12Status: result.section12Status,
      });
    }
  }

  return expandedResults;
}

type CurrentUserRecipientContext = {
  userId: string;
  userEmail: string;
  userTeamId?: string | null;
};

function rebuildUserSearchResult(
  result: NewResultWithoutAccreditation,
  contexts: UserSearchResult["contexts"],
): NewResultWithoutAccreditation {
  const primaryContext = contexts[0];
  const hasSingleContext = contexts.length === 1;

  return {
    ...result,
    teamId: hasSingleContext ? (primaryContext?.id ?? "") : "",
    teamType: primaryContext?.teamType ?? result.teamType,
    contexts,
    teamName: hasSingleContext
      ? (primaryContext?.name ?? undefined)
      : undefined,
    isTrainingOrganisation:
      primaryContext?.isTrainingOrganisation ?? result.isTrainingOrganisation,
    dataControllerId:
      primaryContext?.dataControllerId ?? result.dataControllerId ?? null,
    dataControllerName:
      primaryContext?.dataControllerName ?? result.dataControllerName ?? null,
  };
}

export function filterSelfRecipientResult(
  result: NewResultWithoutAccreditation,
  currentUser: CurrentUserRecipientContext,
): NewResultWithoutAccreditation | null {
  const isCurrentUserResult =
    result.assignedUserId === currentUser.userId ||
    result.email.toLowerCase() === currentUser.userEmail.toLowerCase();

  if (!isCurrentUserResult) {
    return result;
  }

  if (!currentUser.userTeamId || !result.contexts?.length) {
    return null;
  }

  const filteredContexts = result.contexts.filter(
    (context) => context.id !== currentUser.userTeamId,
  );

  if (filteredContexts.length === 0) {
    return null;
  }

  return rebuildUserSearchResult(result, filteredContexts);
}

export function isSelfSendForSelectedContext({
  selectedEmail,
  selectedTeamId,
  userEmail,
  userTeamId,
}: {
  selectedEmail?: string;
  selectedTeamId?: string;
  userEmail: string;
  userTeamId?: string | null;
}): boolean {
  return (
    !!selectedEmail &&
    selectedEmail.toLowerCase() === userEmail.toLowerCase() &&
    (!selectedTeamId || !userTeamId || selectedTeamId === userTeamId)
  );
}

export interface NewFormAssigneeDialogProps {
  formId: string | undefined;
  formContext: Omit<FormContextData, "patient">;
  reloadFormContext: () => void;
  availableActions: (CreateWorkItemAction | { type: "share" })[];
  formDetails: FormDetails;
  userId: string;
  /** The current user's email address, used to prevent self-sending */
  userEmail: string;
  /** The current user's active team context ID */
  userTeamId?: string | null;
  /** The current user's data controller ID from their session context */
  userDataControllerId?: string | null;
  /** The current user's data controller name from their session context */
  userDataControllerName?: string | null;
  onSave: (formData: InferType<typeof newFormAssigneeData>) => Promise<void>;
  searchAssignmentOptions: (
    formDetails: FormDetails,
    query: string,
  ) => Promise<AssignmentSearchResponse>;
  guestMessage?: string;
  guestBannerType?: BannerList;
  setInviteSuccessMessageName?: (params: any) => void;
  guestAccreditationOptions?: { label: string; value: string }[];
  searchResults: NewResultWithoutAccreditation[];
  setSearchResults: Dispatch<SetStateAction<NewResultWithoutAccreditation[]>>;
  navigate: NavigateFunction;
  signForm?: () => void;
  recipients?: InferType<typeof newFormAssigneeData>[];
  setRecipients?: Dispatch<
    SetStateAction<InferType<typeof newFormAssigneeData>[]>
  >;
  hideSendButton?: boolean;
  mentalHealthCareProviders?: MentalHealthCareProvider[];
  /** If true, after sending, redirect to S12 payment request dialog */
  openS12PaymentRequest?: boolean;
  /** Outcome type for S12 payment request */
  s12OutcomeType?: string;
  /** Form ID for S12 payment request */
  s12FormId?: string;
  /** Whether the data controller warning feature is enabled for this org */
  dataControllerWarningEnabled?: boolean;
  /** Optional content rendered between RecipientsList and SendRequestButton (e.g. interview linking) */
  preSubmitContent?: ReactNode;
  /** Interview ID to link recipients to after send (drFinder flow) */
  selectedInterviewId?: string | null;
  /** When true, renders a single "Send Invite" button instead of the multi-select accumulator */
  singleSelectMode?: boolean;
  /** Called when a recipient is selected in single-select mode */
  onRecipientSelected?: (
    formValues: InferType<typeof newFormAssigneeData>,
  ) => void;
  /** Called per search result to determine if it should be disabled (greyed out with tooltip) */
  isOptionDisabled?: (option: NewResultWithoutAccreditation) => {
    disabled: boolean;
    reason?: string;
  };
}

// Check if the membership is not part of the 'soad' team
function isNotSoad(
  membership: { teamType?: string },
  formType: string,
): boolean {
  if (formType !== "soad-assessment") {
    return membership.teamType !== "soad";
  }
  return true;
}

function isAllowedContext(
  membership: { role?: string; teamType?: string },
  formType: FormContextData["type"],
): boolean {
  const notSoad = isNotSoad(membership, formType);

  return membership.role !== "manager" && notSoad;
}

function FormikInner({
  formId,
  formContext,
  reloadFormContext,
  availableActions,
  navigate,
  userId,
  userEmail,
  userTeamId,
  userDataControllerId,
  userDataControllerName,
  dataControllerWarningEnabled,
  formDetails,
  searchAssignmentOptions,
  searchResults,
  setSearchResults,
  mentalHealthCareProviders,
  guestMessage,
  guestBannerType,
  setInviteSuccessMessageName,
  defaultGuestAccreditationValue,
  defaultedGuestAccreditationOptions,
  signForm,
  formikValues,
  initialValues,
  recipients,
  setRecipients,
  hideSendButton,
  openS12PaymentRequest,
  s12OutcomeType,
  s12FormId,
  preSubmitContent,
  selectedInterviewId,
  singleSelectMode,
  onRecipientSelected,
  isOptionDisabled,
}: NewFormAssigneeDialogProps & {
  formikValues: FormikProps<InferType<typeof newFormAssigneeData>>;
  initialValues: InferType<typeof newFormAssigneeData>;
  defaultGuestAccreditationValue: string;
  defaultedGuestAccreditationOptions: { label: string; value: string }[];
}) {
  const { values, setValues, setTouched, touched, errors } = formikValues;

  // Tracks the mental health care provider the user picked from the typeahead.
  // Held in a ref (not state) so it survives the setValues form-resets without
  // re-renders, and so the exact selected id is used — we do NOT re-derive the
  // id from email, since provider emails are not guaranteed unique.
  const selectedProviderRef = useRef<{ id: string; email: string } | undefined>(
    undefined,
  );

  // Resolves the mental health care provider id for a recipient email. Uses the
  // EXACT id the user picked from the typeahead (held in selectedProviderRef),
  // matched by the selected provider's email — so it does not depend on provider
  // emails being unique. Returns undefined when the email isn't the selected
  // provider's, which clears any stale id. Used by every code path that resets
  // the form values via setValues (the search match handler and the email
  // typeahead handler), so the linkage survives whether the provider resolves to
  // a guest or to a full EMHA user.
  const providerIdForEmail = useCallback((email?: string) => {
    const selected = selectedProviderRef.current;
    return selected &&
      email?.trim().toLowerCase() === selected.email.toLowerCase()
      ? selected.id
      : undefined;
  }, []);

  const { t } = useTranslation();

  // Get user context for favourites functionality
  const userContext = useContext(LoggedInUserContext);
  const favouriteRecipients = userContext?.user.favouriteRecipients ?? [];
  const sessionDataControllerId =
    userContext?.user.sessionContext?.dataControllerId;
  const sessionDataControllerName =
    userContext?.user.sessionContext?.dataControllerName;

  // Helper to check if an option is a favourite for the current data controller
  const isFavourite = useCallback(
    (option: { teamId: string; assignedUserId?: string }): boolean => {
      if (!sessionDataControllerId) return false;
      return favouriteRecipients.some(
        (fav) =>
          fav.dataControllerId === sessionDataControllerId &&
          fav.teamId === option.teamId &&
          (option.assignedUserId
            ? fav.userId === option.assignedUserId
            : !fav.userId),
      );
    },
    [favouriteRecipients, sessionDataControllerId],
  );

  // Helper to toggle favourite status
  const handleToggleFavourite = useCallback(
    async (
      option: {
        teamId: string;
        assignedUserId?: string;
        name: string;
        email: string;
        teamName?: string;
        contexts?: NewResultWithoutAccreditation["contexts"];
      },
      event: MouseEvent,
    ): Promise<void> => {
      event.stopPropagation(); // Prevent selecting the option
      event.preventDefault();

      if (
        !userContext ||
        !sessionDataControllerId ||
        !sessionDataControllerName
      )
        return;

      const isCurrentlyFavourite = isFavourite(option);

      if (isCurrentlyFavourite) {
        const removed = await removeFavouriteRecipient(
          sessionDataControllerId,
          option.teamId,
          option.assignedUserId,
          userContext.refetchUser,
        );
        if (!removed) return;
        // The results are host-owned state that the refetched favourites do not
        // rebuild, so drop the unstarred row here
        setSearchResults((prev) =>
          prev.filter(
            (r) =>
              !(
                r.teamId === option.teamId &&
                r.assignedUserId === option.assignedUserId
              ),
          ),
        );
        logActionToDatadog("favourite_recipient_removed", {
          dataControllerId: sessionDataControllerId,
          dataControllerName: sessionDataControllerName,
          teamId: option.teamId,
          teamName: option.teamName,
          userId: option.assignedUserId,
          userName: option.assignedUserId ? option.name : undefined,
        });
      } else {
        // Find the team context for additional info
        const teamContext = option.contexts?.find(
          (c) => c.id === option.teamId,
        );
        const favourite: AddFavouriteRecipientRequest = {
          dataControllerId: sessionDataControllerId,
          dataControllerName: sessionDataControllerName,
          teamId: option.teamId,
          ...(option.assignedUserId && {
            userId: option.assignedUserId,
          }),
        };
        const added = await addFavouriteRecipient(
          favourite,
          userContext.refetchUser,
        );
        if (!added) return;
        logActionToDatadog("favourite_recipient_added", {
          dataControllerId: sessionDataControllerId,
          dataControllerName: sessionDataControllerName,
          teamId: option.teamId,
          teamName: option.teamName ?? teamContext?.name ?? option.name,
          userId: option.assignedUserId,
          userName: option.assignedUserId ? option.name : undefined,
        });
      }
    },
    [
      userContext,
      isFavourite,
      sessionDataControllerId,
      sessionDataControllerName,
      setSearchResults,
    ],
  );

  const emailRegex = new RegExp(
    "^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$",
  );

  const showMentalHealthCareProviders =
    mentalHealthCareProviders && mentalHealthCareProviders.length > 0;
  const [
    mentalHealthCareProvidersInstance,
    setMentalHealthCareProvidersInstance,
  ] = useState(v4());

  // State for showing "keep typing" hint when user has typed 1-2 characters
  const [showKeepTypingHint, setShowKeepTypingHint] = useState(false);

  // Show hint after 1.5 seconds if user has typed 1-2 characters
  useEffect(() => {
    const inputLength = values.email?.length ?? 0;
    if (inputLength > 0 && inputLength < 3) {
      const timer = setTimeout(() => {
        setShowKeepTypingHint(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setShowKeepTypingHint(false);
    }
  }, [values.email]);

  const isTeamSelectorDisabled = values.userMembershipContext.length <= 1;

  const isGuestType = values.type === "guest";

  const filteredMembershipContext = values.userMembershipContext.filter((m) =>
    isAllowedContext(m, formContext.type),
  );

  const onlyMembershipContext =
    filteredMembershipContext.length === 1
      ? filteredMembershipContext[0]
      : undefined;
  const selectedValue =
    onlyMembershipContext !== undefined
      ? onlyMembershipContext.email
      : filteredMembershipContext.length === 0 || isGuestType
        ? values.email
        : values.selectedTeam;

  const isSelectedUserWorkingIndependently =
    values.email === selectedValue && values.teamType === "independent";

  const search = useCallback(
    async (value: string) => {
      const response = await searchAssignmentOptions(formDetails, value);

      const expandedResults = isAnsweredSearch(response)
        ? newExpandSearchResults(response.data.results, formContext.type)
        : [];

      const filteredResults = expandedResults.flatMap((result) => {
        const filteredResult = filterSelfRecipientResult(result, {
          userId,
          userEmail,
          userTeamId,
        });

        return filteredResult ? [filteredResult] : [];
      });

      // Sort favourites to the top
      const sortedResults = [...filteredResults].sort((a, b) => {
        const aIsFav = isFavourite(a);
        const bIsFav = isFavourite(b);
        if (aIsFav && !bIsFav) return -1;
        if (!aIsFav && bIsFav) return 1;
        return 0;
      });

      setSearchResults(sortedResults);

      const match = filteredResults.find(
        (u) => value.toLowerCase() === u.email.toLowerCase(),
      );

      if (match) {
        setInviteSuccessMessageName?.(match);
        if (match.teamType === "guest") {
          setValues({
            type: "guest",
            email: match.email,
            name: match.name,
            userMembershipContext: [],
            guestInviteAccreditation: "",
            mentalHealthCareProviderId: providerIdForEmail(match.email),
          });
        } else {
          setValues({
            type: "full-user",
            userMembershipContext: match.contexts ?? [],
            guestInviteAccreditation: "",
            ...match,
            mentalHealthCareProviderId: providerIdForEmail(match.email),
          });
        }
      }
    },
    [
      searchAssignmentOptions,
      formDetails,
      formContext.type,
      userId,
      userEmail,
      userTeamId,
      isFavourite,
      providerIdForEmail,
      setSearchResults,
      setInviteSuccessMessageName,
      setValues,
    ],
  );

  const debouncedSearch = useCallback(debounce(search, 100), [search]);

  const onEmailTypeaheadInputChanged = useCallback(
    async (value: string, guestNameHint?: string) => {
      const match = searchResults.find(
        (u) => value === `${u.name} (${u.email})`,
      );

      if (match) {
        const userContexts = match.contexts ?? [];
        setInviteSuccessMessageName?.(match?.name);

        // Log if a favourite was used
        if (isFavourite(match)) {
          logActionToDatadog("favourite_recipient_used", {
            dataControllerId: sessionDataControllerId,
            teamId: match.teamId,
            teamName: match.teamName,
            userId: match.assignedUserId,
            userName: match.assignedUserId ? match.name : undefined,
            entityType: match.entityType,
          });
        }

        if (match.teamType === "guest") {
          await setValues({
            type: "guest",
            email: match.email,
            name: guestNameHint ?? match.name,
            userMembershipContext: [],
            guestInviteAccreditation: defaultGuestAccreditationValue,
            mentalHealthCareProviderId: providerIdForEmail(match.email),
          });
        } else {
          await setValues({
            type: "full-user",
            userMembershipContext: userContexts.length > 0 ? userContexts : [],
            guestInviteAccreditation: defaultGuestAccreditationValue,
            ...match,
            mentalHealthCareProviderId: providerIdForEmail(match.email),
          });
        }
      } else {
        setInviteSuccessMessageName?.(value);
        await setValues({
          ...initialValues,
          email: value,
          type: value?.match(emailRegex) ? "guest" : "full-user",
          ...(guestNameHint ? { name: guestNameHint } : {}),
          mentalHealthCareProviderId: providerIdForEmail(value),
        });
      }
      // Only search when we have at least 3 characters
      if (value.length > 2) {
        await debouncedSearch(value);
      } else {
        // Show filtered favourites when < 3 characters
        if (favouriteRecipients.length > 0 && sessionDataControllerId) {
          const lowerValue = value.toLowerCase();
          const filteredFavourites: NewResultWithoutAccreditation[] =
            favouriteRecipients
              .filter(
                (fav) =>
                  fav.dataControllerId === sessionDataControllerId &&
                  (!value ||
                    fav.teamName.toLowerCase().includes(lowerValue) ||
                    fav.teamEmail.toLowerCase().includes(lowerValue) ||
                    (fav.userName &&
                      fav.userName.toLowerCase().includes(lowerValue)) ||
                    (fav.userEmail &&
                      fav.userEmail.toLowerCase().includes(lowerValue))),
              )
              .map((fav) => ({
                teamId: fav.teamId,
                // Repository populates teamType via inner join on teams; the
                // "independent" fallback is just a type-safe default that
                // avoids appending a misleading team-name suffix if a legacy
                // payload arrives without teamType.
                teamType: fav.teamType ?? "independent",
                email: fav.userEmail ?? fav.teamEmail,
                name: fav.userId
                  ? `${fav.userName} (${fav.teamName})`
                  : fav.teamName,
                teamName: fav.teamName,
                assignedUserId: fav.userId,
                isTrainingOrganisation: false,
                entityType: fav.userId ? "user" : "team",
                dataControllerId: null,
                dataControllerName: null,
              }));
          setSearchResults(filteredFavourites);
        } else {
          setSearchResults([]);
        }
      }
    },
    [
      debouncedSearch,
      defaultGuestAccreditationValue,
      emailRegex,
      favouriteRecipients,
      initialValues,
      isFavourite,
      searchResults,
      sessionDataControllerId,
      setInviteSuccessMessageName,
      setSearchResults,
      setValues,
    ],
  );

  return (
    <>
      <Box>
        <Box>
          {showMentalHealthCareProviders && (
            <MentalHealthCareProvidersTypeahead
              key={
                // This allows us to create a new instance of the typeahead
                // when the Add Recipient button has been clicked.
                mentalHealthCareProvidersInstance
              }
              mentalHealthCareProviders={mentalHealthCareProviders}
              onProviderChange={(provider) => {
                // Remember the exact provider the user picked so the recipient
                // can be tagged with its id, even after onEmailTypeaheadInputChanged
                // resets the form values (e.g. when the provider resolves to a
                // full EMHA user selected from the search results).
                selectedProviderRef.current =
                  provider.id && provider.email
                    ? { id: provider.id, email: provider.email }
                    : undefined;

                // Just pretend that the user typed the email address in, and let
                // the rest of the flow continue as normal (passing the name as a
                // hint for the guest name).
                void onEmailTypeaheadInputChanged(
                  provider.email ?? "",
                  provider.name,
                );
              }}
            />
          )}
          <Fade in={showKeepTypingHint} timeout={300}>
            <Typography
              variant="caption"
              sx={{
                color: "primary.main",
                mb: 1,
                display: "block",
                textAlign: "right",
                fontWeight: 500,
                animation: "pulseText 2s ease-in-out infinite",
                "@keyframes pulseText": {
                  "0%, 100%": {
                    opacity: 0.75,
                  },
                  "50%": {
                    opacity: 1,
                  },
                },
              }}
            >
              {t("pages.formDraftSignPage.keepTypingHint")}
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: "1.5em",
                  textAlign: "left",
                  "&::after": {
                    content: '"..."',
                    animation: "dots 0.9s steps(4, end) infinite",
                    "@keyframes dots": {
                      "0%, 20%": { content: '""' },
                      "40%": { content: '"."' },
                      "60%": { content: '".."' },
                      "80%, 100%": { content: '"..."' },
                    },
                  },
                }}
              />
            </Typography>
          </Fade>
          <Typeahead
            enableFuse={false}
            testId="form-assignee-email"
            label={t("pages.formDraftSignPage.typeaheadLabel")}
            options={searchResults}
            name="recipientEmail"
            onBlur={() => {
              setTouched({ ...touched, email: true });
            }}
            onFocus={() => {
              // Show favourites immediately when the input is focused
              if (
                favouriteRecipients.length > 0 &&
                searchResults.length === 0 &&
                sessionDataControllerId
              ) {
                const favouritesForCurrentDataController =
                  favouriteRecipients.filter(
                    (fav) => fav.dataControllerId === sessionDataControllerId,
                  );
                if (favouritesForCurrentDataController.length === 0) return;
                const favouritesAsResults: NewResultWithoutAccreditation[] =
                  favouritesForCurrentDataController.map((fav) => ({
                    teamId: fav.teamId,
                    teamType: fav.teamType ?? "independent",
                    email: fav.userEmail ?? fav.teamEmail,
                    name: fav.userId
                      ? `${fav.userName} (${fav.teamName})`
                      : fav.teamName,
                    teamName: fav.teamName,
                    assignedUserId: fav.userId,
                    isTrainingOrganisation: false,
                    entityType: fav.userId ? "user" : "team",
                    dataControllerId: null,
                    dataControllerName: null,
                  }));
                const lowerUserEmail = userEmail.toLowerCase();
                setSearchResults(
                  favouritesAsResults.filter(
                    (u) =>
                      u.assignedUserId !== userId &&
                      u.email.toLowerCase() !== lowerUserEmail,
                  ),
                );
              }
            }}
            errorMessage={touched.email ? (errors.email as string) : ""}
            showHelperText={!!errors.email && values.email.length >= 5}
            inputValue={values.email}
            optionsKey="name"
            getOptionDisabled={
              isOptionDisabled
                ? (option) => {
                    if (typeof option === "string") return false;
                    return isOptionDisabled(option).disabled;
                  }
                : undefined
            }
            renderOption={(props, option) => {
              const disabledState = isOptionDisabled?.(option);
              const subOptionToShow = () => {
                if (option?.contexts?.length && option?.contexts?.length > 1) {
                  const filteredContexts = option.contexts.filter((context) =>
                    isAllowedContext(context, formContext.type),
                  );
                  return `${filteredContexts.length} team memberships`;
                }

                return option.email;
              };
              const hasOneTeamShow = () => {
                const filteredContexts = option?.contexts?.filter((context) =>
                  isAllowedContext(context, formContext.type),
                );
                const onlyContext =
                  filteredContexts?.length === 1
                    ? filteredContexts[0]
                    : undefined;
                if (
                  onlyContext !== undefined &&
                  onlyContext.teamType !== "independent"
                ) {
                  return `${onlyContext.name}`;
                }
              };

              // Show favourite star for:
              // - Team results: always (can add/remove)
              // - User results with single team context: always (team is determinable)
              // - User results with multiple teams: only if already a favourite (can remove)
              const isTeamResult = option.entityType === "team";
              const filteredContexts = option?.contexts?.filter((context) =>
                isAllowedContext(context, formContext.type),
              );
              const userHasSingleTeam =
                option.entityType === "user" && filteredContexts?.length === 1;
              const isAlreadyFavourite = isFavourite(option);
              const showFavouriteStar =
                isTeamResult || userHasSingleTeam || isAlreadyFavourite;

              return (
                <Tooltip
                  title={disabledState?.reason ?? ""}
                  disableHoverListener={!disabledState?.disabled}
                  placement="top"
                >
                  <li
                    {...props}
                    style={{
                      ...props.style,
                      ...(disabledState?.disabled
                        ? { opacity: 0.5 }
                        : undefined),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {showFavouriteStar && (
                        <Tooltip
                          title={
                            isAlreadyFavourite
                              ? t(
                                  "pages.formDraftSignPage.favourites.removeFavouriteTooltip",
                                )
                              : t(
                                  "pages.formDraftSignPage.favourites.addFavouriteTooltip",
                                )
                          }
                          placement="top"
                        >
                          <IconButton
                            aria-label={
                              isAlreadyFavourite
                                ? t(
                                    "pages.formDraftSignPage.favourites.removeFavourite",
                                  )
                                : t(
                                    "pages.formDraftSignPage.favourites.addFavourite",
                                  )
                            }
                            size="medium"
                            onClick={(event) =>
                              handleToggleFavourite(option, event)
                            }
                            sx={{ mr: 1 }}
                          >
                            {isAlreadyFavourite ? (
                              <Star color="warning" sx={{ fontSize: 28 }} />
                            ) : (
                              <StarBorder sx={{ fontSize: 28 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Avatar
                        sx={{ mr: 2, backgroundColor: "primary.main" }}
                        {...stringAvatar(option.name)}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                          }}
                        >
                          <Typography>{option.name}</Typography>
                          {hasOneTeamShow() && (
                            <Typography sx={{ ml: 1 }}>
                              ({hasOneTeamShow()})
                            </Typography>
                          )}
                        </Box>
                        <Typography>{subOptionToShow()}</Typography>
                      </Box>
                    </Box>
                  </li>
                </Tooltip>
              );
            }}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : `${option.name} (${option.email})`
            }
            onInputChange={onEmailTypeaheadInputChanged}
          />
        </Box>
        {/* Team context selector - only shown when user has multiple team memberships */}
        {filteredMembershipContext.length > 1 && !isTeamSelectorDisabled && (
          <Box sx={{ minWidth: 350 }}>
            <Typeahead
              enableFuse={true}
              testId="form-assignee-team-context"
              label={"Please select a team"}
              options={filteredMembershipContext.filter(
                (m) => m.role === "member",
              )}
              name="teamContext"
              disabled={values.email === ""}
              inputValue={
                values.selectedTeam
                  ? (filteredMembershipContext.find(
                      (m) => m.email === values.selectedTeam,
                    )?.name ?? "")
                  : ""
              }
              optionsKey="name"
              renderOption={(props, context) => {
                const teamId = context.id;
                const isFav = teamId
                  ? isFavourite({
                      teamId,
                      assignedUserId: values.assignedUserId,
                    })
                  : false;

                return (
                  <li {...props}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      {teamId && (
                        <Tooltip
                          title={
                            isFav
                              ? t(
                                  "pages.formDraftSignPage.favourites.removeFavouriteTooltip",
                                )
                              : t(
                                  "pages.formDraftSignPage.favourites.addFavouriteTooltip",
                                )
                          }
                          placement="top"
                        >
                          <IconButton
                            aria-label={
                              isFav
                                ? t(
                                    "pages.formDraftSignPage.favourites.removeFavourite",
                                  )
                                : t(
                                    "pages.formDraftSignPage.favourites.addFavourite",
                                  )
                            }
                            size="medium"
                            onClick={(event) =>
                              handleToggleFavourite(
                                {
                                  teamId,
                                  assignedUserId: values.assignedUserId,
                                  name: values.name,
                                  email: values.email,
                                  teamName: context.name,
                                },
                                event,
                              )
                            }
                            sx={{ mr: 1 }}
                          >
                            {isFav ? (
                              <Star color="warning" sx={{ fontSize: 28 }} />
                            ) : (
                              <StarBorder sx={{ fontSize: 28 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Avatar
                        sx={{ mr: 2, backgroundColor: "primary.main" }}
                        {...stringAvatar(context.name ?? "")}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                        }}
                      >
                        <Typography>
                          {context.teamType === "independent"
                            ? "Independent"
                            : context.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {context.email}
                        </Typography>
                      </Box>
                    </Box>
                  </li>
                );
              }}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : option.teamType === "independent"
                    ? "Independent"
                    : (option.name ?? "")
              }
              onChange={(context) => {
                if (context && typeof context !== "string") {
                  setValues({
                    ...values,
                    isTrainingOrganisation: context.isTrainingOrganisation,
                    name: values.name,
                    selectedTeam: context.email,
                    teamType: context.teamType,
                    teamName: context.name ?? "",
                    teamId: context.id,
                    dataControllerId: context.dataControllerId,
                    dataControllerName: context.dataControllerName,
                  });
                }
              }}
            />
            {isSelectedUserWorkingIndependently && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                  display: "block",
                }}
              >
                Person is working independently
              </Typography>
            )}
          </Box>
        )}
        <Box>
          {values.type === "guest" && (
            <>
              <Box sx={{ mt: 0.5, mb: 4 }}>
                <Box sx={{ mb: 0.5 }}>
                  <TextField
                    useFullWidth={true}
                    value={values.name}
                    onBlur={() => {
                      setTouched({ ...touched, name: true });
                    }}
                    showHelperText={!!errors.name}
                    errorMessage={touched.name ? (errors.name as string) : ""}
                    name={"name"}
                    label={"Name"}
                    onChange={(e) => setValues({ ...values, name: e })}
                  />
                </Box>
                <Banner
                  bannerType={guestBannerType || BannerList.INFO}
                  title={
                    guestMessage ||
                    t("components.newFormAssignee.guestUserClarification")
                  }
                />
              </Box>

              {defaultedGuestAccreditationOptions.length > 1 && (
                <Box sx={{ my: 4 }}>
                  <Dropdown
                    label={"Please confirm who you are sending this invite to"}
                    selectedValue={values.guestInviteAccreditation}
                    name={"invite"}
                    values={defaultedGuestAccreditationOptions}
                    onChange={(e) => {
                      setValues({
                        ...values,
                        guestInviteAccreditation: e,
                      });
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
      {singleSelectMode && onRecipientSelected ? (
        <SingleSelectSubmitFn
          formContext={formContext}
          formikValues={{
            ...formikValues,
            values: {
              ...formikValues.values,
              email: formikValues.values.email.toLowerCase(),
              name: formikValues.values.name.trim(),
            },
          }}
          userDataControllerId={userDataControllerId}
          userDataControllerName={userDataControllerName}
          dataControllerWarningEnabled={dataControllerWarningEnabled}
          userEmail={userEmail}
          userTeamId={userTeamId}
          onRecipientSelected={onRecipientSelected}
        />
      ) : (
        <SubmitComponentFn
          hideSendButton={hideSendButton}
          recipients={recipients ?? []}
          setRecipients={setRecipients ?? (() => {})}
          formContext={formContext}
          formikValues={{
            ...formikValues,
            values: {
              ...formikValues.values,
              email: formikValues.values.email.toLowerCase(),
              name: formikValues.values.name.trim(),
            },
          }}
          availableActions={availableActions}
          formId={formId}
          initialValues={initialValues as FormikInitialValues}
          reloadFormContext={reloadFormContext}
          navigate={navigate}
          signForm={signForm}
          setMentalHealthCareProvidersInstance={
            setMentalHealthCareProvidersInstance
          }
          openS12PaymentRequest={openS12PaymentRequest}
          s12OutcomeType={s12OutcomeType}
          s12FormId={s12FormId}
          userDataControllerId={userDataControllerId}
          userDataControllerName={userDataControllerName}
          dataControllerWarningEnabled={dataControllerWarningEnabled}
          userEmail={userEmail}
          userTeamId={userTeamId}
          preSubmitContent={preSubmitContent}
          selectedInterviewId={selectedInterviewId}
        />
      )}
    </>
  );
}

export function NewFormAssigneeDialog(props: NewFormAssigneeDialogProps) {
  const defaultedGuestAccreditationOptions = !props.guestAccreditationOptions
    ?.length
    ? [
        {
          label: "Other",
          value: "other",
        },
      ]
    : props.guestAccreditationOptions;

  const defaultGuestAccreditationValue =
    defaultedGuestAccreditationOptions.length === 1
      ? (defaultedGuestAccreditationOptions[0]?.value ?? "")
      : "";

  const initialValues = {
    type: "full-user",
    teamId: "",
    email: "",
    name: "",
    guestInviteAccreditation: defaultGuestAccreditationValue,
    isTrainingOrganisation: false,
    selectedTeam: "",
    userMembershipContext: [],
    dataControllerId: null,
    dataControllerName: null,
  } satisfies InferType<typeof newFormAssigneeData>;

  return (
    <Formik<InferType<typeof newFormAssigneeData>>
      validationSchema={newFormAssigneeData}
      initialValues={initialValues}
      onSubmit={props.onSave}
    >
      {(formikValues) => {
        return (
          <FormikInner
            {...props}
            defaultGuestAccreditationValue={defaultGuestAccreditationValue}
            defaultedGuestAccreditationOptions={
              defaultedGuestAccreditationOptions
            }
            formikValues={formikValues}
            initialValues={initialValues}
          />
        );
      }}
    </Formik>
  );
}

function SingleSelectSubmitFn({
  formContext,
  formikValues,
  userDataControllerId,
  userDataControllerName,
  dataControllerWarningEnabled,
  userEmail,
  userTeamId,
  onRecipientSelected,
}: {
  formContext: Omit<FormContextData, "patient">;
  formikValues: FormikNewFormAssigneeValues;
  userDataControllerId?: string | null;
  userDataControllerName?: string | null;
  dataControllerWarningEnabled?: boolean;
  userEmail: string;
  userTeamId?: string | null;
  onRecipientSelected: (
    formValues: InferType<typeof newFormAssigneeData>,
  ) => void;
}) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isTrainingContext = formContext.isTrainingFormContext;
  const { dataSharingPartnerships } = useContext(ManagedLocationsContext);

  const userNotSelected = userNotSelectedFn(formikValues);

  const shareToNonTrainingUserFromTraining =
    shareToNonTrainingUserFromTrainingFn(
      formikValues,
      userNotSelected,
      isTrainingContext,
    );

  const shareToTrainingUserFromNonTraining =
    shareToTrainingUserFromNonTrainingFn(
      formikValues,
      userNotSelected,
      isTrainingContext,
    );

  const isShareOutsideDataController =
    dataControllerWarningEnabled &&
    isShareOutsideDataControllerFn(
      formikValues,
      userNotSelected,
      userDataControllerId,
      dataSharingPartnerships,
    );

  const isShareToIndependent =
    dataControllerWarningEnabled &&
    isShareToIndependentFn(formikValues, userNotSelected);

  const currentSelectionCandidate = isShareOutsideDataController
    ? {
        teamType: formikValues.values.teamType,
        teamName: formikValues.values.teamName,
        name: formikValues.values.name,
        dataControllerId: formikValues.values.dataControllerId,
        dataControllerName: formikValues.values.dataControllerName,
      }
    : null;

  const recipientsOutsideDataController = dataControllerWarningEnabled
    ? getRecipientsOutsideDataController({
        userDataControllerId,
        currentSelection: currentSelectionCandidate,
        recipients: [],
        dataSharingPartnerships,
      })
    : [];

  const independentRecipientNames = dataControllerWarningEnabled
    ? isShareToIndependent
      ? [(formikValues.values.teamName ?? formikValues.values.name) || ""]
      : []
    : [];

  const isSelfSend = isSelfSendForSelectedContext({
    selectedEmail: formikValues.values.email,
    selectedTeamId: formikValues.values.teamId,
    userEmail,
    userTeamId,
  });

  const canSend = !(
    userNotSelected ||
    isSelfSend ||
    formikValues.isSubmitting ||
    shareToNonTrainingUserFromTraining ||
    shareToTrainingUserFromNonTraining
  );

  return (
    <Box>
      {submitError && (
        <Box sx={{ mb: 4 }}>
          <Banner title={submitError} bannerType={BannerList.ERROR} />
        </Box>
      )}

      <TrainingWarningBanners
        shareToNonTrainingUserFromTraining={shareToNonTrainingUserFromTraining}
        shareToTrainingUserFromNonTraining={shareToTrainingUserFromNonTraining}
      />

      <DataControllerWarningBanners
        userDataControllerName={userDataControllerName}
        recipientsOutsideDataController={recipientsOutsideDataController}
        independentRecipientNames={independentRecipientNames}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          mt: 2,
        }}
      >
        <Button
          testId="single-select-send-invite-button"
          label="Proceed"
          disabled={!canSend}
          onClick={() => {
            setSubmitError(null);
            onRecipientSelected(formikValues.values);
          }}
        />
      </Box>
    </Box>
  );
}

export function SubmitComponentFn({
  formContext,
  formikValues,
  availableActions,
  formId,
  initialValues,
  reloadFormContext,
  navigate,
  signForm,
  recipients,
  setRecipients,
  hideSendButton,
  setMentalHealthCareProvidersInstance,
  openS12PaymentRequest,
  s12OutcomeType,
  s12FormId,
  userDataControllerId,
  userDataControllerName,
  dataControllerWarningEnabled,
  userEmail,
  userTeamId,
  preSubmitContent,
  selectedInterviewId,
}: {
  formContext: Omit<FormContextData, "patient">;
  formikValues: FormikNewFormAssigneeValues;
  availableActions: (CreateWorkItemAction | { type: "share" })[];
  formId: string | undefined;
  initialValues: FormikInitialValues;
  reloadFormContext: () => void;
  navigate: (path: string) => void;
  signForm?: () => void;
  recipients: InferType<typeof newFormAssigneeData>[];
  setRecipients: Dispatch<
    SetStateAction<InferType<typeof newFormAssigneeData>[]>
  >;
  hideSendButton?: boolean;
  setMentalHealthCareProvidersInstance: (param: string) => void;
  /** If true, after sending, redirect to S12 payment request dialog */
  openS12PaymentRequest?: boolean;
  /** Outcome type for S12 payment request */
  s12OutcomeType?: string;
  /** Form ID for S12 payment request */
  s12FormId?: string;
  /** The current user's data controller ID from their session context */
  userDataControllerId?: string | null;
  /** The current user's data controller name from their session context */
  userDataControllerName?: string | null;
  /** Whether the data controller warning feature is enabled for this org */
  dataControllerWarningEnabled?: boolean;
  /** The current user's email address, used to prevent self-sending */
  userEmail: string;
  /** The current user's active team context ID */
  userTeamId?: string | null;
  /** Optional content rendered between RecipientsList and SendRequestButton */
  preSubmitContent?: ReactNode;
  /** Interview ID to link recipients to after send (drFinder flow) */
  selectedInterviewId?: string | null;
}) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isTrainingContext = formContext.isTrainingFormContext;
  const { isMobileView } = useScreenDetection();
  const { dataSharingPartnerships, emailRedirects } = useContext(
    ManagedLocationsContext,
  );
  const guestInviteAccreditation = {
    type: mapGuestAccreditation(
      formContext,
      formikValues.values.guestInviteAccreditation,
    ),
  } as CreateWorkItemAction | { type: "other" };

  const userNotSelected = userNotSelectedFn(formikValues);

  const shareToNonTrainingUserFromTraining =
    shareToNonTrainingUserFromTrainingFn(
      formikValues,
      userNotSelected,
      isTrainingContext,
    );

  const shareToTrainingUserFromNonTraining =
    shareToTrainingUserFromNonTrainingFn(
      formikValues,
      userNotSelected,
      isTrainingContext,
    );

  // Only calculate data controller warnings if the feature is enabled
  const isShareOutsideDataController =
    dataControllerWarningEnabled &&
    isShareOutsideDataControllerFn(
      formikValues,
      userNotSelected,
      userDataControllerId,
      dataSharingPartnerships,
    );

  const isShareToIndependent =
    dataControllerWarningEnabled &&
    isShareToIndependentFn(formikValues, userNotSelected);

  // Build the current selection as a RecipientCandidate if it's outside data controller
  const currentSelectionCandidate = isShareOutsideDataController
    ? {
        teamType: formikValues.values.teamType,
        teamName: formikValues.values.teamName,
        name: formikValues.values.name,
        dataControllerId: formikValues.values.dataControllerId,
        dataControllerName: formikValues.values.dataControllerName,
      }
    : null;

  // Accumulate warnings from all added recipients plus current selection
  const recipientsOutsideDataController = dataControllerWarningEnabled
    ? getRecipientsOutsideDataController({
        userDataControllerId,
        currentSelection: currentSelectionCandidate,
        recipients: recipients.map((r) => ({
          teamType: r.teamType,
          teamName: r.teamName,
          name: r.name,
          dataControllerId: r.dataControllerId,
          dataControllerName: r.dataControllerName,
        })),
        dataSharingPartnerships,
      })
    : [];

  // Accumulate independent recipients from all added recipients plus current selection
  const independentRecipientNames = dataControllerWarningEnabled
    ? [
        // Include current selection if it's independent
        ...(isShareToIndependent
          ? [(formikValues.values.teamName ?? formikValues.values.name) || ""]
          : []),
        // Include all added recipients that are independent
        ...recipients
          .filter((recipient) => recipient.teamType === "independent")
          .map((recipient) => (recipient.teamName ?? recipient.name) || ""),
      ]
    : [];

  const isSelfSend = isSelfSendForSelectedContext({
    selectedEmail: formikValues.values.email,
    selectedTeamId: formikValues.values.teamId,
    userEmail,
    userTeamId,
  });

  const canAddRecipient = !(
    userNotSelected ||
    isSelfSend ||
    formikValues.isSubmitting ||
    shareToNonTrainingUserFromTraining ||
    shareToTrainingUserFromNonTraining
  );

  const isSoadFormContext = formContext.type === "soad-assessment";

  const formType = formContext.type;

  return (
    <Box>
      {submitError && (
        <Box sx={{ mb: 4 }}>
          <Banner title={submitError} bannerType={BannerList.ERROR} />
        </Box>
      )}

      <TrainingWarningBanners
        shareToNonTrainingUserFromTraining={shareToNonTrainingUserFromTraining}
        shareToTrainingUserFromNonTraining={shareToTrainingUserFromNonTraining}
      />

      <DataControllerWarningBanners
        userDataControllerName={userDataControllerName}
        recipientsOutsideDataController={recipientsOutsideDataController}
        independentRecipientNames={independentRecipientNames}
      />

      <AddRecipientButton
        disabled={!canAddRecipient}
        onClick={async () => {
          setSubmitError(null);

          // Log cross-org data sharing
          if (isShareOutsideDataController) {
            logActionToDatadog("cross_org_data_sharing", {
              sourceDataControllerId: userDataControllerId,
              sourceDataControllerName: userDataControllerName,
              targetDataControllerId: formikValues.values.dataControllerId,
              targetDataControllerName: formikValues.values.dataControllerName,
              targetTeamId: formikValues.values.teamId,
              targetTeamName:
                formikValues.values.teamName ?? formikValues.values.name,
              targetUserId: formikValues.values.assignedUserId,
            });
          }

          const action =
            guestInviteAccreditation.type &&
            guestInviteAccreditation.type !== "other"
              ? guestInviteAccreditation.type === "complete"
                ? (availableActions.find(
                    (a) => a.type === "complete" && a.formId === formId,
                  ) as {
                    type: "complete";
                    formId: UUID;
                    formPart: number;
                    formVersion: number;
                  })!
                : guestInviteAccreditation
              : undefined;

          // Ensure that we get a fresh instance of
          // the typeahead every time we add a recipient
          setMentalHealthCareProvidersInstance(v4());

          return handleAddRecipient({
            formikValues,
            setRecipients,
            initialValues,
            action,
            emailRedirects,
          });
        }}
      />
      <RecipientsList
        recipients={recipients}
        isMobileView={isMobileView}
        setRecipients={setRecipients}
        setSubmitError={setSubmitError}
        emailRedirects={emailRedirects}
      />
      {recipients.some((r) =>
        emailRedirects.some(
          (er) => er.sourceEmail.toLowerCase() === r.email?.toLowerCase(),
        ),
      ) && (
        <Box sx={{ mt: 2 }}>
          {emailRedirects
            .filter((er) =>
              recipients.some(
                (r) => er.sourceEmail.toLowerCase() === r.email?.toLowerCase(),
              ),
            )
            .map((er) => (
              <Box key={er.sourceEmail} sx={{ mb: 1 }}>
                <Banner
                  bannerType={BannerList.WARNING}
                  title={
                    er.message ||
                    `This email address has been redirected to ${er.targetTeamName ?? "another team"}. Your form will be sent to the new team.`
                  }
                />
              </Box>
            ))}
        </Box>
      )}
      {preSubmitContent}
      {!hideSendButton && (
        <SendRequestButton
          navigate={navigate}
          shareToNonTrainingUserFromTraining={
            shareToNonTrainingUserFromTraining
          }
          shareToTrainingUserFromNonTraining={
            shareToTrainingUserFromNonTraining
          }
          recipients={recipients}
          setSubmitError={setSubmitError}
          formContextId={formContext.id}
          reloadFormContext={reloadFormContext}
          patientId={formContext.patientId}
          signForm={signForm}
          canAddRecipient={canAddRecipient}
          isSoadFormContext={isSoadFormContext}
          formType={formType}
          openS12PaymentRequest={openS12PaymentRequest}
          s12OutcomeType={s12OutcomeType}
          s12FormId={s12FormId}
          selectedInterviewId={selectedInterviewId}
        />
      )}
    </Box>
  );
}

const MentalHealthCareProvidersTypeahead = ({
  mentalHealthCareProviders,
  onProviderChange,
}: {
  mentalHealthCareProviders: MentalHealthCareProvider[];
  onProviderChange: (args: {
    id?: string;
    name: string;
    email?: string;
  }) => void;
}) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");

  const onInputChange = useCallback(
    (value: string) => {
      setSearchTerm(value);

      const match = mentalHealthCareProviders.find(
        (provider) => provider.name === value,
      );

      if (match) {
        onProviderChange({
          id: match.id,
          name: match.name,
          email: match.email,
        });
      }
    },
    [mentalHealthCareProviders, onProviderChange],
  );

  return (
    <Stack
      sx={{
        marginBottom: 2,
      }}
    >
      <Typeahead
        enableFuse={false}
        testId="form-mental-health-care-provider-name"
        label={t("pages.formDraftSignPage.mhcProviderTypeaheadLabel")}
        allowFreeText
        options={mentalHealthCareProviders}
        inputValue={searchTerm}
        name={"providerName"}
        optionsKey={"name"}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Avatar
                sx={{ mr: 2, backgroundColor: "primary.main" }}
                {...stringAvatar(option.name)}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography>{option.name}</Typography>
                <Typography>{option.email}</Typography>
              </Box>
            </Box>
          </li>
        )}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.name
        }
        onInputChange={onInputChange}
      />
    </Stack>
  );
};
